/**
 * /learn/courses/:courseId — course welcome only.
 *
 * Hero CTA branches on `course.progress.bucket` and the per-module cursor:
 *     upcoming     → "Starts on {publish_date}" (read-only)
 *     in_progress  → "Continue course" (resumes at the cursor module)
 *     in_progress with no started module → "Start the course"
 *     completed    → "Review course" (opens the sections page in free-roam)
 *
 * Sections live on /learn/courses/:courseId/sections (CourseSectionsPage)
 * and modules under each section nest as
 * /learn/courses/:courseId/sections/:sectionId/modules/:moduleId.
 */

import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LockClosedIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { fadeIn, slideUp } from '@/lib/motion';
import { useCourse } from '../../hooks/learner/useCourse';
import { useCourseRoutes } from '../../hooks/learner/useCourseRoutes';
import { useImageUrl } from '../../services/uploads';
import PreviewWelcomeHero, { BackPill } from '../../components/programs/preview/PreviewWelcomeHero';
import LearnerCourseProgressBar from '../../components/learner/LearnerCourseProgressBar';
import StickyProgressRow from '../../components/learner/StickyProgressRow';
import CommentsSection from '../../components/comments/CommentsSection';
import { getEnrolmentAvailability, isEnrolmentLocked } from '../../utils/enrolmentAvailability';
import type { components } from '../../types/learnerApi';

type Section = components['schemas']['Section'];
type ContentModule = components['schemas']['ContentModule'];
type EvaluationModule = components['schemas']['EvaluationModule'];
type FeedbackModule = components['schemas']['FeedbackModule'];
type AnyModule = ContentModule | EvaluationModule | FeedbackModule;

function errorCode(err: unknown): string | null {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? null;
}

function moduleStatus(m: AnyModule): 'not_started' | 'in_progress' | 'completed' | undefined {
  return (m as ContentModule).progress?.status as
    | 'not_started'
    | 'in_progress'
    | 'completed'
    | undefined;
}

function sortModules(modules: AnyModule[]): AnyModule[] {
  return modules.slice().sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
}

const CourseDetailPage: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId?: string }>();
  const id = assignmentId ?? '';
  const navigate = useNavigate();
  const routes = useCourseRoutes(id);
  const { data: course, isLoading, error } = useCourse(id);
  const [coverUrl] = useImageUrl(course?.image_url ?? null);

  // Enrolment date-window gate.
  const availability = useMemo(() => {
    return getEnrolmentAvailability(course?.assignment, {
      completed: course?.progress?.bucket === 'completed',
    });
  }, [course]);

  const sortedSections = useMemo<Section[]>(() => {
    return (course?.sections ?? [])
      .slice()
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, [course]);

  // Cursor: first in_progress module, else first not_started.
  const cursor = useMemo(() => {
    let firstNotStarted: { sectionId: string; moduleId: string } | null = null;
    for (const section of sortedSections) {
      const sortedModules = sortModules((section.modules ?? []) as AnyModule[]);
      for (const m of sortedModules) {
        const status = moduleStatus(m);
        if (status === 'in_progress' && m.id && section.id) {
          return { sectionId: section.id, moduleId: m.id };
        }
        if (!firstNotStarted && (!status || status === 'not_started') && m.id && section.id) {
          firstNotStarted = { sectionId: section.id, moduleId: m.id };
        }
      }
    }
    return firstNotStarted;
  }, [sortedSections]);

  const firstModule = useMemo(() => {
    for (const section of sortedSections) {
      const sortedModules = sortModules((section.modules ?? []) as AnyModule[]);
      if (sortedModules.length > 0 && sortedModules[0].id && section.id) {
        return { sectionId: section.id, moduleId: sortedModules[0].id };
      }
    }
    return null;
  }, [sortedSections]);

  if (isLoading) return <CourseDetailSkeleton />;

  const code = errorCode(error);
  if (code === 'NOT_AVAILABLE_YET' || (error && !course)) {
    const publishDate = (
      error as { response?: { data?: { publish_date?: string } } }
    )?.response?.data?.publish_date;
    return (
      <motion.div {...fadeIn} className="max-w-2xl mx-auto text-center py-24 space-y-4">
        <LockClosedIcon className="w-16 h-16 text-text-tertiary mx-auto" />
        <h1 className="text-2xl font-bold text-text-primary">Course Not Yet Available</h1>
        <p className="text-text-secondary">
          {publishDate
            ? `This course opens on ${new Date(publishDate).toLocaleDateString()}.`
            : 'This course is not yet available to you.'}
        </p>
        <button
          onClick={() => navigate(routes.coursesIndex)}
          className="mx-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-sm font-bold hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to My Courses
        </button>
      </motion.div>
    );
  }

  if (!course) return null;

  // Date-window gate: only a course that hasn't OPENED yet locks the screen.
  // A passed end date never blocks (learners on leave finish late; everyone
  // may review) — it renders as an informational banner further down instead.
  if (isEnrolmentLocked(availability)) {
    const dateStr = availability.startsAt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return (
      <motion.div {...fadeIn} className="max-w-2xl mx-auto text-center py-24 space-y-4">
        <LockClosedIcon className="w-16 h-16 text-text-tertiary mx-auto" />
        <h1 className="text-2xl font-bold text-text-primary">Course Not Yet Available</h1>
        <p className="text-text-secondary">{`This course opens on ${dateStr}.`}</p>
        <button
          onClick={() => navigate(routes.coursesIndex)}
          className="mx-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-sm font-bold hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to My Courses
        </button>
      </motion.div>
    );
  }

  const endedBanner =
    availability.kind === 'expired' ? (
      <div
        data-testid="course-ended-banner"
        className="rounded-xl border border-warning-500/30 bg-warning-500/10 px-4 py-3 text-sm text-text-primary"
      >
        This course&rsquo;s end date passed on{' '}
        {availability.endedAt.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
        . You can still review and finish it — your progress and results still
        count.
      </div>
    ) : null;

  const bucket = course.progress?.bucket ?? 'in_progress';
  const isCompleted = bucket === 'completed';
  const isInProgress = bucket === 'in_progress';
  const hasStartedModule = sortedSections.some((s) =>
    (s.modules ?? []).some((m) => {
      const status = moduleStatus(m as AnyModule);
      return status === 'in_progress' || status === 'completed';
    })
  );
  const ctaLabel: string | null = isCompleted
    ? 'Review course'
    : isInProgress && hasStartedModule
      ? 'Continue course'
      : firstModule
        ? 'Start the course'
        : null;

  const handleStart = () => {
    if (!id) return;
    // Continue / Start opens the sections grid focused on the cursor section
    // (or the first section if nothing's started yet). Review mode opens the
    // first section. From there the learner picks a section to drill into.
    const target = cursor ?? firstModule;
    if (target?.sectionId) {
      navigate(`${routes.base}/sections/${target.sectionId}`);
    } else {
      navigate(`${routes.base}/sections`);
    }
  };

  const durationLabel =
    course.estimated_duration_minutes != null
      ? `${course.estimated_duration_minutes} min`
      : undefined;

  return (
    <motion.div {...slideUp} className="w-[95%] sm:w-[90%] mx-auto py-6 space-y-6">
      <div className="px-1">
        <BackPill
          label="Go back to course library"
          onClick={() => navigate(routes.coursesIndex)}
        />
      </div>
      {course.progress && (
        <StickyProgressRow>
          <div className="px-1">
            <LearnerCourseProgressBar
              percent={course.progress.progress_percent ?? 0}
              modulesCompleted={course.progress.modules_completed ?? 0}
              modulesTotal={course.progress.modules_total ?? 0}
            />
          </div>
        </StickyProgressRow>
      )}

      {endedBanner}

      <PreviewWelcomeHero
        coverUrl={coverUrl ?? undefined}
        title={course.title ?? 'Course'}
        description={course.subtitle ?? undefined}
        durationLabel={durationLabel}
        compact
        cta={
          ctaLabel
            ? { label: ctaLabel, onClick: handleStart }
            : undefined
        }
      />

      {/* Comments are course-scoped (shared across all enrolments of the same
          course), so we target the underlying `course.id` rather than the
          enrolment-scoped `assignment_id` from the URL. */}
      {course.id && <CommentsSection targetType="course" targetId={course.id} />}
    </motion.div>
  );
};

export default CourseDetailPage;

function CourseDetailSkeleton() {
  return (
    <div className="w-[95%] sm:w-[90%] mx-auto py-6 space-y-6 animate-pulse">
      <div className="h-4 w-32 rounded bg-text-tertiary/10" />
      <div className="aspect-[16/10] rounded-[28px] bg-text-tertiary/10" />
      <div className="h-3 w-2/3 rounded bg-text-tertiary/10" />
    </div>
  );
}
